import React, { useState } from "react";
// import ContactMap from "./ContactMap";

export default function ContactSection() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Check if this is the first submission (we'll use localStorage to track this)
      const isFirstSubmission = !localStorage.getItem('formSubmitted');

      if (isFirstSubmission) {
        // For the first submission, we'll use the traditional form submission
        // This will allow FormSubmit to send the activation email
        localStorage.setItem('formSubmitted', 'true');

        // Submit the form traditionally
        e.target.submit();
        return; // Exit early as the page will reload
      }

      // For subsequent submissions, use AJAX
      const response = await fetch('https://formsubmit.co/ajax/aa21f22bd858af2b66556ae63df9e108', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject || "New Contact Form Submission",
          message: formData.message,
          _template: "table",
          _captcha: "false"
        })
      });

      if (response.ok) {
        setSubmitStatus({ success: true, message: "Message sent successfully! We'll get back to you soon." });
        // Reset form
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          subject: "",
          message: ""
        });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error("Error sending email:", error);
      setSubmitStatus({
        success: false,
        message: "There was a problem sending your message. Please try again or contact us directly."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 md:pt-[230px]">
      <div className="theme-container w-full mx-auto grid grid-cols-7 lg:grid-cols-12 gap-y-16 lg:gap-y-0 lg:gap-x-[70px]">
        {/* <!-- address start  --> */}
        <div className="col-span-7 lg:col-span-5 flex flex-col items-start">
          <h1 className="font-regular text-main-black text-[35px] text-center">
            Let's get started
          </h1>

          <p className="text-paragraph mt-[30px]">
            Don't worry about having things all figured out. Tell us your goals, and we can help you get there.
          </p>

          <div className="w-full text-center flex justify-start items-center mt-10 ">
            <div className="font-medium text-paragraph space-y-4">
              <div className="flex items-center gap-3">
                <i className="fa-solid fa-phone text-purple" aria-hidden="true"></i>
                <a 
                  href="tel:+14072843646" 
                  className="hover:text-purple transition-colors"
                  aria-label="Call us at 1-407-284-3646"
                >
                  1-407-284-3646
                </a>
              </div>
              <div className="flex items-center gap-3">
                <i className="fa-solid fa-envelope text-purple" aria-hidden="true"></i>
                <a 
                  href="mailto:hello@informulate.ai"
                  className="hover:text-purple transition-colors"
                  aria-label="Email us at hello@informulate.ai"
                >
                  hello@informulate.ai
                </a>
              </div>
              <div className="flex items-center gap-3">
                <i className="fa-solid fa-location-dot text-purple"></i>
                <span>Orlando, FL</span>
              </div>
            </div>
          </div>



          <div>
            <img className="w-96 -ml-10 mt-10" src={require("../../assets/images/logo.png")} alt="" />
          </div>
          <hr className="border-purple/10 mt-11 mb-[15px]" />
          <div className="flex items-center gap-[15px]">
            <h1 className="font-semibold text-18 text-main-black pr-2.5">
              Follow Us
            </h1>
            <a
              href="https://www.facebook.com/Informulate/"
              target="blank"
              aria-label="facebook"
              className="w-10 h-10 text-purple hover:text-white rounded-full flex justify-center items-center border border-[#2858b9]/10 overflow-hidden relative before:inline-block before:absolute before:z-0 before:w-full before:h-full before:bg-[#2858b9] before:scale-x-0 group hover:before:scale-x-100 before:origin-right hover:before:origin-left before:transition-transform before:ease-out before:duration-300"
            >
              <span className="relative z-10">
                <svg
                  width="11"
                  height="16"
                  viewBox="0 0 11 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.6667 0H8.55556C5.79413 0 3.55556 2.23857 3.55556 5V6.22222H0V9.77778H3.55556V16H7.11111V9.77778H10.6667V6.22222H7.11111V4.55556C7.11111 4.00327 7.55883 3.55556 8.11111 3.55556H10.6667V0Z"
                    fill="currentColor"
                  />
                </svg>
              </span>
            </a>
            <a
              href="https://mobile.twitter.com/informulate?lang=en"
              aria-label="twitter"
              target="blank"
              className="w-10 h-10 text-purple hover:text-white rounded-full flex justify-center items-center border border-[#2858b9]/10 overflow-hidden relative before:inline-block before:absolute before:z-0 before:w-full before:h-full before:bg-[#2858b9] before:scale-x-0 group hover:before:scale-x-100 before:origin-right hover:before:origin-left before:transition-transform before:ease-out before:duration-300"
            >
              <span className="relative z-10">
                <svg
                  width="19"
                  height="17"
                  viewBox="0 0 19 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12.7642 0C10.5722 0 8.7953 1.86585 8.7953 4.1675C8.7953 4.5153 8.83587 4.85315 8.91232 5.17611C6.80469 5.17611 3.63013 4.74999 0.978868 2.09376C0.626315 1.74054 -0.0237835 1.9767 0.000670803 2.47516C0.393588 10.484 3.82353 12.8202 5.58986 12.9656C4.44926 14.0921 2.79242 14.9813 1.1252 15.3804C0.685191 15.4857 0.576494 15.9674 1.00675 16.1073C2.19973 16.4953 3.90729 16.6448 4.82642 16.67C11.3286 16.67 16.6134 11.1972 16.731 4.3991C17.5847 3.84394 18.1315 2.63855 18.4388 1.78464C18.5136 1.57667 18.1728 1.33436 17.9687 1.41931C17.331 1.68479 16.5214 1.74773 15.8318 1.52302C15.1039 0.593104 14 0 12.7642 0Z"
                    fill="currentColor"
                  />
                </svg>
              </span>
            </a>
            <a
              href="https://www.youtube.com/channel/UCZpg4EfBwVG2JjngH84nlRA"
              aria-label="youtube"
              target="blank"
              className="w-10 h-10 text-purple hover:text-white rounded-full flex justify-center items-center border border-[#2858b9]/10 overflow-hidden relative before:inline-block before:absolute before:z-0 before:w-full before:h-full before:bg-[#2858b9] before:scale-x-0 group hover:before:scale-x-100 before:origin-right hover:before:origin-left before:transition-transform before:ease-out before:duration-300"
            >
              <span className="relative z-10">
                <svg
                  width="18"
                  height="13"
                  viewBox="0 0 18 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.6225 2.0801C17.4169 1.26166 16.7735 0.618265 15.9551 0.412704C14.5495 0.0517578 9 0.0517578 9 0.0517578C9 0.0517578 3.45055 0.0517578 2.04495 0.412704C1.22652 0.618265 0.583141 1.26166 0.377579 2.0801C0.0166321 3.48569 0.0166321 6.49993 0.0166321 6.49993C0.0166321 6.49993 0.0166321 9.51416 0.377579 10.9198C0.583141 11.7382 1.22652 12.3816 2.04495 12.5872C3.45055 12.9481 9 12.9481 9 12.9481C9 12.9481 14.5495 12.9481 15.9551 12.5872C16.7735 12.3816 17.4169 11.7382 17.6225 10.9198C17.9834 9.51416 17.9834 6.49993 17.9834 6.49993C17.9834 6.49993 17.9834 3.48569 17.6225 2.0801ZM7.15955 9.29576V3.70409L11.8399 6.49993L7.15955 9.29576Z"
                    fill="currentColor"
                  />
                </svg>
              </span>
            </a>
            <a
              href="https://www.linkedin.com/company/informulate-llc/"
              aria-label="linkedin"
              target="blank"
              className="w-10 h-10 text-purple hover:text-white rounded-full flex justify-center items-center border border-[#2858b9]/10 overflow-hidden relative before:inline-block before:absolute before:z-0 before:w-full before:h-full before:bg-[#2858b9] before:scale-x-0 group hover:before:scale-x-100 before:origin-right hover:before:origin-left before:transition-transform before:ease-out before:duration-300"
            >
              <span className="relative z-10">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.32083 16H0.245V5.31533H3.32083V16ZM1.78125 3.8595C0.797667 3.8595 0 2.98383 0 1.92983C0 0.875833 0.797667 0 1.78125 0C2.76483 0 3.5625 0.875833 3.5625 1.92983C3.5625 2.98383 2.76483 3.8595 1.78125 3.8595ZM15.9958 16H12.9283V10.8C12.9283 9.56383 12.9033 7.97633 11.3142 7.97633C9.70033 7.97633 9.44833 9.31533 9.44833 10.7037V16H6.37667V5.31533H9.31833V6.7595H9.36667C9.82583 5.91867 10.8892 5.02867 12.4675 5.02867C15.58 5.02867 16 7.23617 16 10.0845V16H15.9958Z"
                    fill="currentColor"
                  />
                </svg>
              </span>
            </a>
          </div>
        </div>
        {/* <!-- address end  --> */}
        {/* <!-- contact form start --> */}
        <div className="col-span-7">
          <div className="border border-purple/10 bg-main-gray rounded-[10px] p-4 md:p-[50px]">
            <h1 className="text-[30px] tracking-tight font-semibold text-main-black pb-6">
              Send Us Message
            </h1>
            {/* <p className="text-paragraph mb-[30px]">
              Don't worry, your email address is never shared.
            </p> */}

            {submitStatus && (
              <div className={`p-4 mb-6 rounded-md ${submitStatus.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {submitStatus.message}
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              action="https://formsubmit.co/aa21f22bd858af2b66556ae63df9e108"
              method="POST"
              className="grid grid-cols-6 md:grid-cols-12 gap-[30px]"
            >
              {/* FormSubmit configuration fields */}
              <input type="hidden" name="_subject" value="New Contact Form Submission from Informulate Website" />
              <input type="hidden" name="_captcha" value="false" />
              <input type="hidden" name="_template" value="table" />
              <input type="hidden" name="_next" value={window.location.href} />

              <div className="col-span-6 relative">
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Full Name"
                  className={`w-full h-[30px] focus:outline-none border-b ${errors.fullName ? 'border-red-500' : 'border-purple/10 focus:border-purple'} bg-transparent`}
                />
                {errors.fullName && (
                  <p className="text-red-500 text-xs mt-1 absolute">{errors.fullName}</p>
                )}
              </div>

              <div className="col-span-6 relative">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email"
                  className={`w-full h-[30px] focus:outline-none border-b ${errors.email ? 'border-red-500' : 'border-purple/10 focus:border-purple'} bg-transparent`}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1 absolute">{errors.email}</p>
                )}
              </div>

              <div className="col-span-6">
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Phone Number"
                  className="w-full h-[30px] focus:outline-none border-b border-purple/10 focus:border-purple bg-transparent"
                />
              </div>

              <div className="col-span-6">
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Subject"
                  className="w-full h-[30px] focus:outline-none border-b border-purple/10 focus:border-purple bg-transparent"
                />
              </div>

              <div className="col-span-6 md:col-span-12 relative">
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Comments"
                  className={`w-full focus:outline-none border-b ${errors.message ? 'border-red-500' : 'border-purple/10 focus:border-purple'} bg-transparent h-[83px]`}
                ></textarea>
                {errors.message && (
                  <p className="text-red-500 text-xs mt-1 absolute">{errors.message}</p>
                )}
              </div>

              <div className="col-span-6 md:col-span-12">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="home-two-btn-bg py-3 group bg-[#4A7DFF] border-[#4A7DFF] inline-flex disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  <span className="text-base text-white group-hover:text-[#4A7DFF] transition-all duration-300 font-inter relative z-10">
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </span>
                  <svg
                    className="relative z-10"
                    width="7"
                    height="12"
                    viewBox="0 0 7 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      className="group-hover:stroke-[#4A7DFF] transition-all duration-300"
                      d="M1.10254 10.5L4.89543 6.70711C5.22877 6.37377 5.39543 6.20711 5.39543 6C5.39543 5.79289 5.22877 5.62623 4.89543 5.29289L1.10254 1.5"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                  </svg>
                </button>
              </div>
            </form>
          </div>
        </div>
        {/* <!-- contact-form end  --> */}
      </div>
    </section>
  );
}
